import { useMemo } from "react"
import { dequal } from "dequal"
import { useStableValue } from "~/hooks/useStableValue"
import { useSafeLocalStorage, useSafeSessionStorage } from "~/hooks/useSafeLocalStorage"
import { z } from "zod"
import {
  BUY_GROUP_BY_VALUES,
  type BuyGroupBy,
  resolveDefaultAggregations,
  resolveDefaultBuyGroupBy,
} from "~/utils/productTableUtils"
import { useCompany } from "~/hooks/useCompany"
import {
  FASHION_GROUP_BY_VALUES,
  type FashionGroupBy,
} from "~/components/purchase-plan/fashionGrouping"

// Module-level: stable identities keep the storage hooks' validate effects from
// re-running on every render.
const STRING_ARRAY_SCHEMA = z.array(z.string())
const BUY_GROUP_BY_SCHEMA = z.enum(BUY_GROUP_BY_VALUES)
const FASHION_GROUP_BY_SCHEMA = z.enum(FASHION_GROUP_BY_VALUES)
const EMPTY_COLUMNS: string[] = []

// Both storage hooks run unconditionally (rules of hooks); the flags ensure only the
// active one persists on init. Reset is asymmetric by design: session removes the key,
// local (which has no remover) writes the default back.
const useProductTableStorage = (
  key: string,
  initialValue: string[],
  storage: "session" | "local",
  persistOnInit: boolean,
): [string[], (value: string[]) => void, () => void] => {
  const isLocal = storage === "local"
  const sessionTuple = useSafeSessionStorage<string[]>(
    key,
    initialValue,
    STRING_ARRAY_SCHEMA,
    false,
    !isLocal && persistOnInit,
  )
  const [localValue, setLocalValue] = useSafeLocalStorage<string[]>(
    key,
    initialValue,
    STRING_ARRAY_SCHEMA,
    false,
    isLocal && persistOnInit,
  )
  if (isLocal) return [localValue, setLocalValue, () => setLocalValue(initialValue)]
  return sessionTuple
}

export const useProductTableDisplayColumns = (
  id: string,
  defaultColumns: string[] | undefined = undefined,
  storage: "session" | "local" = "session",
): [string[], (value: string[]) => void, () => void] => {
  const hasDefault = (defaultColumns?.length ?? 0) > 0
  return useProductTableStorage(
    id + "displayedColumnIds",
    defaultColumns ?? EMPTY_COLUMNS,
    storage,
    hasDefault,
  )
}

export const useProductTableAggregations = (
  id: string,
  defaultAggregationMethod: string | undefined = undefined,
  storage: "session" | "local" = "session",
  respectCompanyDefault = true,
): [string[], (value: string[]) => void, () => void] => {
  const { data: company, isLoading } = useCompany()
  const companyDefault = respectCompanyDefault ? (company?.defaultAggregationLevel ?? null) : null
  // Memoized: a fresh array every render would re-run the storage hooks' validate effects.
  const initialValue = useMemo(
    () => resolveDefaultAggregations(defaultAggregationMethod, companyDefault),
    [defaultAggregationMethod, companyDefault],
  )
  // While a company default is active, keep it live (don't persist) so a settings change
  // takes effect on the next open instead of being pinned by an old stored value.
  const persistOnInit = defaultAggregationMethod !== undefined && !isLoading && !companyDefault
  const [stored, setStored, reset] = useProductTableStorage(
    id + "aggregations",
    initialValue,
    storage,
    persistOnInit,
  )
  return [useStableValue(stored, dequal), setStored, reset]
}

const useGroupBy = <T extends string>(
  storageKey: string,
  schema: z.ZodType<T>,
  defaultFor: (companyDefault: string | null | undefined) => T,
): [T, (value: T) => void, () => void] => {
  const { data: company, isLoading } = useCompany()
  return useSafeSessionStorage<T>(
    storageKey,
    defaultFor(company?.defaultAggregationLevel),
    schema,
    false,
    !isLoading && !company?.defaultAggregationLevel,
  )
}

/**
 * Row-grouping dimension for the buy tables, defaulting from the company grouping
 * setting. Persisted per session only while no company default is set (keeps it live).
 */
export const useBuyGroupBy = (storageKey: string) =>
  useGroupBy<BuyGroupBy>(storageKey, BUY_GROUP_BY_SCHEMA, resolveDefaultBuyGroupBy)

/** {@link useBuyGroupBy} for the fashion buy table, which can also group by Fabric. */
export const useFashionGroupBy = (storageKey: string) =>
  useGroupBy<FashionGroupBy>(storageKey, FASHION_GROUP_BY_SCHEMA, resolveDefaultBuyGroupBy)
